import * as mobx from 'mobx';
import React from 'react';
import { observer } from 'mobx-react';
import { EventBlueprintFormVM } from './event-blueprint-form.viewmodel';
import { RichTextEditor } from '../../../global/rich-text-editor';
import { CommonPopupBodyFooterLayout } from '../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../global/sub-window/common/common-close-sub-window-buttons';
import { FormattedMessage, useIntl } from 'react-intl';
import { FieldKey, FORM_CONFIG } from './event-blueprint-form.config';
import { FormField } from '../../../global/form/form-field/form-field';
import { SelectTimezones } from '../../../global/select-timezone/select-timezones';
import { eventBusinessLines, eventRegions, eventTypes } from '../../../../constants/EventValues';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import OptionsButton from '../../../global/options-button/options-button';
import './event-blueprint.form.scss';
import { OEToggle } from '../../../global/oe-toggle/oe-toggle';
import Text from '../../../global/text/text';
import { EnablementStatus } from '../../../../constants/common.constants';
import UploadFile from '../../../_events/_manage-event/shared/upload-file/upload-file';
import { FILE_TYPE } from '../../../_events/_manage-event/shared/upload-button';
import { S3ObjectMetadata } from '../../../../api/shared.dto';
import { EventTypesMultiselect } from './event-types-multiselect/event-types-multiselect.container';
import {
    attendeeLinkTypeOptions,
    eventAccessOptions,
    passportURLFormatOptions
} from '../../../../constants/blueprint-labels.constants';
import { MORE_BUTTON_TEXT, cancelText, submitText } from '../../../../constants/common-labels.constants';
import { ViewBlueprintDetailsSection } from '../../../_events/_manage-event/shared/view-blueprint-details-section/view-blueprint-details-section';
import { DEFAULT_MEETING_TEMPLATE_EXPLENATION, NO_TEMPLATE_SELECTED_TEXT } from '../../blueprints.texts';
import { ParticipantLinkType } from './event-blueprint.mapper';
import { EventAttributesMultiselect } from './event-attributes-multiselect/event-attributes-multiselect.container';
import DefaultButton from '../../../global/default-button/default-button';
import { RESPONSIVE_EMAILS_TEXT } from './order-booking-emails/order-booking-emails.texts';

export const participantLinkOptions = (): { value: string; label: string }[] => {
    const intl = useIntl();
    return [
        {
            value: ParticipantLinkType.PASSPORT_LINK,
            label: intl.formatMessage({
                id: 'oe.blueprints.event.participantLink.passport',
                defaultMessage: 'OE Passport Link',
                description: 'Label text'
            })
        },
        {
            value: ParticipantLinkType.MEETING_LINK_WITH_EMBEDDED_PASSCODE,
            label: intl.formatMessage({
                id: 'oe.blueprints.event.participantLink.meeting',
                defaultMessage: 'OE Meeting Link with Embedded Passcode',
                description: 'Label text'
            })
        }
    ];
};

export type EventBlueprintFormPresentationProps = {
    vm: EventBlueprintFormVM;
};

export const EventBlueprintFormPresentation = observer(({ vm }: EventBlueprintFormPresentationProps): JSX.Element => {
    const intl = useIntl();

    const {
        name,
        description,
        timeZone,
        region,
        businessLine,
        type,
        eventAccess,
        defaultMeetingDuration,
        createPassportSite,
        createSingleMeeting,
        meetingBlueprintId,
        passportConfig,
        integrationEnabled,
        externalId,
        attendeeLinkType,
        passportUrlFormat,
        integrationMeetingBlueprintId,
        orderBookingEnabled,
        publicName,
        publicDescription,
        participantLinkType,
        defaultMeetingBlueprintEnabled,
        defaultMeetingBlueprintId
    } = vm.getFormFields(FieldKey);

    const hasDeletePermission = hasPermission(Permission.DELETE_EVENT_BLUEPRINTS);

    const blueprintOptionsButton = [
        hasDeletePermission && {
            label: intl.formatMessage({
                id: `oe.blueprint.deleteBluePrint`,
                description: 'Label text',
                defaultMessage: 'Delete Blueprint',
            }),
            action: vm.deleteBluePrint,
            danger: true
        }
    ].filter(Boolean);

    const toggleCreatePassportSite = (): void => {
        createPassportSite.value ? vm.disableCreatePassportSite() : vm.enableCreatePassportSite();
    };

    const toggleCreateSingleMeeting = (): void => {
        createSingleMeeting.value ? vm.disableCreateSingleMeeting() : vm.enableCreateSingleMeeting();
    };

    const passportConfigAsFile = passportConfig.value ? { name: (passportConfig.value as S3ObjectMetadata).title } : null;

    const toggleIntegration = (): void => {
        integrationEnabled.value ? vm.disableIntegration() : vm.enableIntegration();
    };

    const toggleOrderBooking = (): void => {
        orderBookingEnabled.value ? vm.disableOrderBooking() : vm.enableOrderBooking();
    };

    return (
        <CommonPopupBodyFooterLayout
            body={
                <form className='oe-form oe-form--event-blueprint'>
                    <div className='oe-form__body'>
                        <FormField
                            className='m-bottom-20px'
                            label={<FormattedMessage {...FORM_CONFIG[FieldKey.NAME].labelDescriptor} />}
                        >
                            <input
                                className='oe-form-field__control'
                                value={name.value as string}
                                type='text'
                                onChange={e => name.setValue(e.target.value)}
                                onBlur={name.setTouched}
                            />
                            <span className='oe-form-field__error'>
                                {
                                    !name.valid && name.touched &&
                                    <FormattedMessage
                                        values={{ fieldName: intl.formatMessage(FORM_CONFIG[FieldKey.NAME].labelDescriptor) }}
                                        {...name.validationMessage}
                                    />
                                }
                            </span>
                        </FormField>
                        <FormField
                            className='m-bottom-20px'
                            label={<FormattedMessage {...FORM_CONFIG[FieldKey.DESCRIPTION].labelDescriptor} />}
                        >
                            <input
                                className='oe-form-field__control'
                                value={description.value as string}
                                type='text'
                                onChange={e => description.setValue(e.target.value)}
                                onBlur={description.setTouched}
                            />
                            <span className='oe-form-field__error'>
                                {
                                    !description.valid && description.touched &&
                                    <FormattedMessage
                                        values={{ fieldName: intl.formatMessage(FORM_CONFIG[FieldKey.DESCRIPTION].labelDescriptor) }}
                                        {...description.validationMessage}
                                    />
                                }
                            </span>
                        </FormField>
                        <div className='oe-form__row m-bottom-20px'>
                            <FormField
                                label={<FormattedMessage {...FORM_CONFIG[FieldKey.TIME_ZONE].labelDescriptor} />}
                                className='w-40'
                            >
                                <SelectTimezones
                                    value={timeZone.value as string}
                                    onChange={tz => timeZone.setValue(tz)}
                                    className='oe-form-field__control'
                                    placeHolderHidden={false}
                                    placeHolderDisabled={false}
                                    placeHolder={
                                        intl.formatMessage({
                                            id: `oe.timezone.selectTimezone`,
                                            description: 'Placeholder text',
                                            defaultMessage: 'Select timezone',
                                        })
                                    }
                                />
                            </FormField>
                            <FormField
                                label={<FormattedMessage {...FORM_CONFIG[FieldKey.REGION].labelDescriptor} />}
                                className='w-40'
                            >
                                <select
                                    className='oe-form-field__control'
                                    value={region.value as string}
                                    onChange={e => region.setValue(e.target.value)}
                                >
                                    <option value={''}>
                                        {intl.formatMessage({
                                            id: `oe.event.noRegionSelected`,
                                            description: 'Label text',
                                            defaultMessage: 'No Region Selected',
                                        })}
                                    </option>
                                    {
                                        eventRegions(intl).map(({ label }, i) => {
                                            return <option value={label} key={i}>{label}</option>;
                                        })
                                    }
                                </select>
                            </FormField>
                        </div>
                        <div className='oe-form__row m-bottom-20px'>
                            <FormField
                                className='w-40'
                                label={<FormattedMessage {...FORM_CONFIG[FieldKey.BUSINESS_LINE].labelDescriptor} />}
                            >
                                <select
                                    className='oe-form-field__control'
                                    value={businessLine.value as string}
                                    onChange={e => businessLine.setValue(e.target.value)}
                                >
                                    <option value={''}>
                                        {intl.formatMessage({
                                            id: `oe.event.noBusinessLineSelected`,
                                            description: 'Label text',
                                            defaultMessage: 'No Business Line Selected',
                                        })}
                                    </option>
                                    {
                                        eventBusinessLines(intl).map(({ label }, i) => {
                                            return <option value={label} key={i}>{label}</option>;
                                        })
                                    }
                                </select>
                            </FormField>
                            <FormField
                                label={<FormattedMessage {...FORM_CONFIG[FieldKey.TYPE].labelDescriptor} />}
                                className='w-40'
                            >
                                <select
                                    className='oe-form-field__control'
                                    value={type.value as string}
                                    onChange={e => type.setValue(e.target.value)}
                                >
                                    <option value={''}>
                                        {intl.formatMessage({
                                            id: `oe.event.selectEventType`,
                                            description: 'Label text',
                                            defaultMessage: 'Select Event Type',
                                        })}
                                    </option>
                                    {
                                        eventTypes(intl).map(({ label }, i) => {
                                            return <option value={label} key={i}>{label}</option>;
                                        })
                                    }
                                </select>
                            </FormField>
                        </div>
                        <FormField
                            label={<FormattedMessage {...FORM_CONFIG[FieldKey.EVENT_ACCESS].labelDescriptor} />}
                            className='event-access-selector m-bottom-20px'
                        >
                            <select
                                className='oe-form-field__control'
                                value={eventAccess.value as string}
                                onChange={e => eventAccess.setValue(e.target.value)}
                            >
                                <option value={''}>
                                    {intl.formatMessage({
                                        id: 'oe.event.selectEventAccess',
                                        description: 'Label text',
                                        defaultMessage: 'Select Event Access',
                                    })}
                                </option>
                                {
                                    eventAccessOptions().map(({ value, label }, i) => {
                                        return <option value={value} key={`${i}_${value}`}>{label}</option>;
                                    })
                                }
                            </select>
                        </FormField>
                        <FormField
                            label={<FormattedMessage {...FORM_CONFIG[FieldKey.DEFAULT_MEETING_DURATION].labelDescriptor} />}
                            className='m-bottom-20px'
                        >
                            <input
                                className='oe-form-field__control'
                                type='number'
                                min={0}
                                max={1440}
                                step={10}
                                value={defaultMeetingDuration.value as number}
                                onChange={e => {
                                    // @ts-ignore
                                    defaultMeetingDuration.setValue((e.currentTarget.value |= 0));
                                }}
                                onBlur={defaultMeetingDuration.setTouched}
                            />
                            <span className='oe-form-field__error'>
                                {
                                    !defaultMeetingDuration.valid && defaultMeetingDuration.touched &&
                                    <FormattedMessage
                                        values={{ fieldName: intl.formatMessage(FORM_CONFIG[FieldKey.DEFAULT_MEETING_DURATION].labelDescriptor) }}
                                        {...defaultMeetingDuration.validationMessage}
                                    />
                                }
                            </span>
                        </FormField>
                        <FormField>
                            <div className='blueprint-toggle'>
                                <OEToggle
                                    isSelected={defaultMeetingBlueprintEnabled.value as boolean}
                                    onClick={() => vm.toggleDefaultMeetingBlueprint(defaultMeetingBlueprintEnabled.value as boolean)}
                                />
                                <Text
                                    message='Default Meeting Template is {prefix}'
                                    id='common.createPassportSite'
                                    description='Label text'
                                    values={{
                                        prefix: defaultMeetingBlueprintEnabled.value ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                    }}
                                />
                            </div>
                            <div className='meeting-template--info-msg'>
                                { intl.formatMessage(DEFAULT_MEETING_TEMPLATE_EXPLENATION) }
                            </div>
                            {defaultMeetingBlueprintEnabled.value &&
                                <FormField
                                    label={ intl.formatMessage(
                                        FORM_CONFIG[FieldKey.DEFAULT_MEETING_BLUEPRINT_ID].labelDescriptor
                                    ) }
                                    className={'m-top-20px'}
                                >
                                    <select
                                        className='oe-form-field__control'
                                        value={defaultMeetingBlueprintId.value as string}
                                        onChange={e => defaultMeetingBlueprintId.setValue(e.target.value)}
                                    >
                                        <option value={''}>
                                            {intl.formatMessage({
                                                id: `oe.templates.noTemplateSelected`,
                                                description: 'Label text',
                                                defaultMessage: 'No Template Selected',
                                            })}
                                        </option>
                                        {
                                            // eslint-disable-next-line @typescript-eslint/no-shadow
                                            vm.meetingTemplates.map(({ blueprintId, name }) => {
                                                return <option value={blueprintId} key={blueprintId}>{name}</option>;
                                            })
                                        }
                                    </select>
                                    <span className='oe-form-field__error'>
                                        {
                                            !defaultMeetingBlueprintId.valid && defaultMeetingBlueprintId.touched &&
                                            <FormattedMessage {...defaultMeetingBlueprintId.validationMessage} />
                                        }
                                    </span>
                                </FormField>
                            }


                        </FormField>
                        <FormField className='m-bottom-20px'>
                            <div className='blueprint-toggle'>
                                <OEToggle
                                    isSelected={createPassportSite.value as boolean}
                                    onClick={toggleCreatePassportSite}
                                />
                                <Text
                                    message='Create Passport Site is {prefix}'
                                    id='common.createPassportSite'
                                    description='Label text'
                                    values={{
                                        prefix: createPassportSite.value ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                    }}
                                />
                            </div>
                            {createPassportSite.value &&
                                <>
                                    <FormField
                                        label={<FormattedMessage {...FORM_CONFIG[FieldKey.PASSPORT_CONFIG].labelDescriptor} />}
                                        className={'m-top-20px'}
                                    >
                                        <UploadFile
                                            file={passportConfigAsFile}
                                            fileType={FILE_TYPE.JSON}
                                            onChange={vm.uploadPassportConfig}
                                            deleteFile={vm.deletePassportConfig}
                                        />
                                        <span className='oe-form-field__error'>
                                            {
                                                !passportConfig.valid && passportConfig.touched &&
                                                <FormattedMessage {...passportConfig.validationMessage} />
                                            }
                                        </span>
                                    </FormField>
                                    <FormField
                                        label={<FormattedMessage {...FORM_CONFIG[FieldKey.PASSPORT_URL_FORMAT].labelDescriptor} />}
                                    >
                                        <select
                                            className='oe-form-field__control'
                                            value={passportUrlFormat.value as string}
                                            onChange={e => passportUrlFormat.setValue(e.target.value)}
                                        >
                                            {
                                                passportURLFormatOptions(intl).map(({ value, label }, i) => {
                                                    return <option value={value} key={`${i}_${value}`}>{label}</option>;
                                                })
                                            }
                                        </select>
                                    </FormField>
                                </>
                            }
                        </FormField>
                        <FormField className='m-bottom-20px'>
                            <div className='blueprint-toggle'>
                                <OEToggle
                                    isSelected={createSingleMeeting.value as boolean}
                                    onClick={toggleCreateSingleMeeting}
                                />
                                <Text
                                    message='Automatically create single meeting is {prefix}'
                                    id='common.createSingleMeeting'
                                    description='Label text'
                                    values={{
                                        prefix: createSingleMeeting.value ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                    }}
                                />
                            </div>
                        </FormField>
                        {
                            createSingleMeeting.value &&
                            <FormField
                                label={<FormattedMessage
                                    id='oe.meeting.template'
                                    defaultMessage='Meeting Template'
                                    description='label'
                                />}
                            >
                                <select
                                    className='oe-form-field__control'
                                    value={meetingBlueprintId.value as string}
                                    onChange={e => meetingBlueprintId.setValue(e.target.value)}
                                >
                                    <option value={''}>
                                        {intl.formatMessage({
                                            id: `oe.templates.noTemplateSelected`,
                                            description: 'Label text',
                                            defaultMessage: 'No Template Selected',
                                        })}
                                    </option>
                                    {
                                        // eslint-disable-next-line @typescript-eslint/no-shadow
                                        vm.meetingTemplates.map(({ blueprintId, name }) => {
                                            return <option value={blueprintId} key={blueprintId}>{name}</option>;
                                        })
                                    }
                                </select>
                                <div className='meeting-template--info-msg'>
                                    <FormattedMessage
                                        id='oe.meetingTemplates.infoMessage'
                                        description='Info Message'
                                        defaultMessage='Event start and end times will be used for meeting times.'
                                    />
                                </div>
                            </FormField>
                        }
                        <FormField className='m-bottom-20px'>
                            <div className='blueprint-toggle'>
                                <OEToggle
                                    isSelected={integrationEnabled.value as boolean}
                                    onClick={toggleIntegration}
                                />
                                <Text
                                    message='Integration is {prefix}'
                                    id='common.integrationToggle'
                                    description='Label text'
                                    values={{
                                        prefix: integrationEnabled.value ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                    }}
                                />
                            </div>
                        </FormField>
                        {
                            integrationEnabled.value &&
                            <FormField
                                className='m-bottom-20px'
                                label={<FormattedMessage {...FORM_CONFIG[FieldKey.EXTERNAL_ID].labelDescriptor} />}
                            >
                                <input
                                    className='oe-form-field__control'
                                    value={externalId.value as string}
                                    type='text'
                                    onChange={e => externalId.setValue(e.target.value)}
                                    onBlur={externalId.setTouched}
                                />
                                <span className='oe-form-field__error'>
                                    {
                                        !externalId.valid && externalId.touched &&
                                        <FormattedMessage
                                            values={{ fieldName: intl.formatMessage(FORM_CONFIG[FieldKey.EXTERNAL_ID].labelDescriptor) }}
                                            {...externalId.validationMessage}
                                        />
                                    }
                                </span>
                            </FormField>
                        }
                        {
                            integrationEnabled.value &&
                            <FormField
                                label={<FormattedMessage {...FORM_CONFIG[FieldKey.INTEGRATION_MEETING_TEMPLATE].labelDescriptor} />}
                            >
                                <select
                                    className='oe-form-field__control'
                                    value={integrationMeetingBlueprintId.value as string}
                                    onChange={e => integrationMeetingBlueprintId.setValue(e.target.value)}
                                >
                                    <option value={''}>
                                        {intl.formatMessage(NO_TEMPLATE_SELECTED_TEXT)}
                                    </option>
                                    {
                                        vm.meetingTemplates.map(template => {
                                            return <option value={template.blueprintId} key={template.blueprintId}>{template.name}</option>;
                                        })
                                    }
                                </select>
                                {
                                    integrationMeetingBlueprintId.value &&
                                    <ViewBlueprintDetailsSection
                                        blueprintDetails={vm.getSelectedMeetingTemplateDetails(integrationMeetingBlueprintId.value as string)}
                                        blueprintItemType={vm.getSelectedTemplate(integrationMeetingBlueprintId.value as string).type}
                                    />
                                }
                            </FormField>
                        }
                        {
                            integrationEnabled.value &&
                            <>
                                <FormField
                                    label={<FormattedMessage {...FORM_CONFIG[FieldKey.ATTENDEE_LINK_TYPE].labelDescriptor} />}
                                    className='m-bottom-20px'
                                >
                                    <select
                                        className='oe-form-field__control'
                                        value={attendeeLinkType.value as string}
                                        onChange={e => attendeeLinkType.setValue(e.target.value)}
                                    >
                                        {
                                            attendeeLinkTypeOptions(intl).map(({ value, label }, i) => {
                                                return <option value={value} key={`${i}_${value}`}>{label}</option>;
                                            })
                                        }
                                    </select>
                                </FormField>
                                <EventTypesMultiselect
                                    selectedEventTypes={vm.getBlueprintEventTypes()}
                                    disabledEventTypes={vm.getExcludedEventTypes()}
                                    setEventTypes={vm.setBlueprintEventTypes}
                                    removeEventType={vm.removeEventType}
                                />
                            </>
                        }
                        <FormField className='m-bottom-20px'>
                            <div className='blueprint-toggle'>
                                <OEToggle
                                    isSelected={orderBookingEnabled.value as boolean}
                                    onClick={toggleOrderBooking}
                                />
                                <Text
                                    message='Order Booking is {prefix}'
                                    id='common.orderBookingToggle'
                                    description='Label text'
                                    values={{
                                        prefix: orderBookingEnabled.value ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                    }}
                                />
                            </div>
                        </FormField>
                        {
                            orderBookingEnabled.value &&
                            <>
                                <FormField
                                    className='m-bottom-20px'
                                    label={<FormattedMessage {...FORM_CONFIG[FieldKey.PUBLIC_NAME].labelDescriptor} />}
                                >
                                    <input
                                        className='oe-form-field__control'
                                        value={publicName.value as string}
                                        type='text'
                                        onChange={e => publicName.setValue(e.target.value)}
                                        onBlur={publicName.setTouched}
                                    />
                                    <span className='oe-form-field__error'>
                                        {
                                            !publicName.valid && publicName.touched &&
                                            <FormattedMessage
                                                id="oe.validation.required.message"
                                                values={{ fieldName: intl.formatMessage(FORM_CONFIG[FieldKey.PUBLIC_NAME].labelDescriptor) }}
                                                defaultMessage="{fieldName} is required."
                                            />
                                        }
                                    </span>
                                </FormField>
                                <FormField
                                    className='m-bottom-20px'
                                    label={<FormattedMessage {...FORM_CONFIG[FieldKey.PUBLIC_DESCRIPTION].labelDescriptor} />}
                                >
                                    <RichTextEditor
                                        id='oe-form-field__control'
                                        className='m-p-reset'
                                        value={publicDescription.value as string}
                                        onChange={mobx.action((event, editor) => {
                                            publicDescription.setValue(editor.getData());
                                        })}
                                        onBlur={publicDescription.setTouched}
                                        validationError={
                                            !publicDescription.valid && publicDescription.touched
                                                ? <FormattedMessage {...publicDescription.validationMessage} />
                                                : undefined
                                        }
                                    />
                                </FormField>
                                {vm.isEditMode &&
                                    <DefaultButton
                                        modifier='fat'
                                        className='responsive-emails'
                                        text={intl.formatMessage(RESPONSIVE_EMAILS_TEXT)}
                                        onClick={vm.showOrderBookingEmailsForm}
                                    />
                                }
                                <FormField
                                    label={<FormattedMessage {...FORM_CONFIG[FieldKey.PARTICIPANT_LINK_TYPE].labelDescriptor} />}
                                    className='m-bottom-20px'
                                >
                                    <select
                                        className='oe-form-field__control'
                                        value={participantLinkType.value as string || ParticipantLinkType.PASSPORT_LINK}
                                        onChange={e => {
                                            participantLinkType.setValue(e.target.value);
                                        }}
                                    >
                                        {
                                            participantLinkOptions().map(({ value, label }, i) => {
                                                return <option value={value} key={`${i}_${value}`}>{label}</option>;
                                            })
                                        }
                                    </select>
                                </FormField>
                                <EventAttributesMultiselect
                                    selectedEventAttributes={vm.getSelectedEventAttributes()}
                                    availableEventAttributes={vm.getAvailableEventAttributes()}
                                    setEventAttributes={vm.setEventAttributes}
                                    removeEventAttribute={vm.removeEventAttribute}
                                />
                            </>
                        }
                    </div>
                </form>
            }
            footer={
                <div className='buttons-block buttons-block--from-right'>
                    <DefaultCloseSubWindowButton
                        text={submitText(vm.isEditMode)}
                        modifier={['fat', 'confirm-safe']}
                        onClick={vm.submitBluePrint}
                        disabled={vm.isLoading}
                    />
                    <DefaultCloseSubWindowButton text={cancelText()} />
                    {vm.isEditMode && !!blueprintOptionsButton.length &&
                        <OptionsButton
                            className='options-button--right-margin-auto'
                            modifier={['fat', 'options']}
                            label={<FormattedMessage {...MORE_BUTTON_TEXT} />}
                            options={blueprintOptionsButton}
                            position='top'
                            align='start'
                        />
                    }
                </div>
            }
        />
    );
});
